import type { PropsWithChildren, ReactNode } from 'react'

type Props = PropsWithChildren<{
  label?: string
  right?: ReactNode
  accent?: 'amber' | 'cyan' | 'violet' | 'red' | 'green' | 'none'
  className?: string
  bodyClassName?: string
}>

const accentMap: Record<NonNullable<Props['accent']>, string> = {
  amber: 'before:bg-amber-glow after:bg-amber-glow',
  cyan: 'before:bg-radar-cyan after:bg-radar-cyan',
  violet: 'before:bg-radar-violet after:bg-radar-violet',
  red: 'before:bg-radar-red after:bg-radar-red',
  green: 'before:bg-radar-green after:bg-radar-green',
  none: 'before:bg-paper-trace after:bg-paper-trace',
}

/**
 * Does `bodyClassName` set its own padding?
 *
 * The default body padding used to be concatenated ahead of
 * `bodyClassName`, which reads like an override but is not one: two
 * padding utilities on the same element are resolved by their order in
 * the stylesheet, not in the class string, and Tailwind emits `p-0`
 * before `p-4`. So every caller passing `p-0` to sit a table flush
 * against the panel edge silently got 16px anyway.
 */
const PADDING_RE = /(?:^|\s)p-\d/

/** A panel with HUD-style corner brackets. `label` floats at the top-left. */
export function Panel({
  label, right, accent = 'amber', className = '', bodyClassName = '', children,
}: Props) {
  const accentClass = accentMap[accent]
  return (
    <section
      className={`relative border border-ink-500 bg-ink-800/60 backdrop-blur-[1px] ${className}`}
      style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.02)' }}
    >
      <BracketCorners className={accentClass} />
      {(label || right) && (
        <header className="flex items-center justify-between border-b border-ink-500/60 px-4 py-2">
          {label && <span className="label-amber">{label}</span>}
          {right && <div className="flex items-center gap-3">{right}</div>}
        </header>
      )}
      <div className={`${PADDING_RE.test(bodyClassName) ? '' : 'p-4'} ${bodyClassName}`}>
        {children}
      </div>
    </section>
  )
}

function BracketCorners({ className }: { className: string }) {
  // Four 10x10 corner brackets made from two absolute-positioned pseudo-ish divs each
  return (
    <>
      <span className={`absolute top-0 left-0 w-[10px] h-[2px] ${className.split(' ')[0]}`} />
      <span className={`absolute top-0 left-0 h-[10px] w-[2px] ${className.split(' ')[0]}`} />

      <span className={`absolute top-0 right-0 w-[10px] h-[2px] ${className.split(' ')[1]}`} />
      <span className={`absolute top-0 right-0 h-[10px] w-[2px] ${className.split(' ')[1]}`} />

      <span className={`absolute bottom-0 left-0 w-[10px] h-[2px] ${className.split(' ')[0]}`} />
      <span className={`absolute bottom-0 left-0 h-[10px] w-[2px] ${className.split(' ')[0]}`} />

      <span className={`absolute bottom-0 right-0 w-[10px] h-[2px] ${className.split(' ')[1]}`} />
      <span className={`absolute bottom-0 right-0 h-[10px] w-[2px] ${className.split(' ')[1]}`} />
    </>
  )
}
